// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from "react";

import api from '../../../api';
import { relativeTime } from '../../../utilities/planSync';

// Components
import Button from './Button';
import Checkbox from '../../Checkbox';
import Sortable from '../../Sortable';
import AccountCard from "./AccountCard";
import PyeCalendarSelector from "./PlanCalendarSelect";
import Loading from "react-loading";

// Styling
import '../../../styles/planeditor.css';
import { toast } from "react-toastify";
import Select from 'react-select';

// Data to be populated in new account object
const newAccount = {
    provider: "",
    number: "",
    payee: "",
    internalPlanId: ""
};

export default class PlanPage extends React.Component {

    abortController = new AbortController();

    state = {
        // Accounts within the selected plan
        accounts:[],
        
        // State to show save button when user is updating plan Information
        updatingPlan : false,

        loadingAccounts: false,
        editingAccountIdx: null, // Index of account being edited in accounts array
        // null or -1 => none selected

        // New account object
        editingAccount: -1, // If null, we're not creating a new account

        // List of investment providers
        investmentProviders: [],

        // Controller for load more accounts text
        loadingMoreAccounts: false,

        // All account count
        maxAccountsCount : 0
    }

    componentDidMount = () => {
        this.loadAccounts(this.props.plan);
        this.loadInvestmentProviders();
    }

    componentDidUpdate = (prev) => {
        if (this.props.plan?._id === prev.plan?._id) {
            return;
        }

        this.setState({
            updatingPlan: false,
            editingAccountIdx: null,
            editingAccount: null
        });

        this.loadAccounts(this.props.plan);
        // this.loadInvestmentProviders();
    }

    loadAccounts = async (plan) => {

        // Load all the accounts based on the planId given
        this.setState({
            loadingAccounts: true
        })

        // Abort the last request sent for loading more accounts
        this.abortController.abort();
        this.abortController = new AbortController();

        // If new plan - skip loading accounts
        if(!plan?._id)
            return this.setState({
                loadingAccounts: false,
                accounts: []
            });

        // Fetch list of accounts for the plan clicked on the Plan Editor
        const res = await api.get(`/tpa/account/list?planId=${plan?._id}&countDocs=true`, {signal: this.abortController.signal}, false, {}, true);

        // If the request got aborted return - keep loadingAccounts spinner
        if (!res || (!res?.success && res.cancelled))
            return

        // Account response handling
        if (!res?.success) {
            toast.error("Failed to load investment accounts");
            this.setState({
                loadingAccounts: false
            })
            return
        }

        // Set accounts and maxAccountsCount
        this.setState({
            loadingAccounts: false,
            accounts: res.accounts,
            maxAccountsCount: res.maxAccountsCount
        })
    }

    // Load more accounts
    loadMoreAccounts = async (plan, mouseEvent) => {
        
        // Persist the scrollbar location
        mouseEvent.stopPropagation();

        this.setState({
            loadingMoreAccounts: true
        })

        // Fetch more accounts
        const res = await api.get(`/tpa/account/list?planId=${plan?._id}&countDocs=true&resultOffset=${this.state.accounts.filter(a=>a._id).length}`);

        // Account response handling
        if (!res?.success) {
            toast.error("Failed to load investment accounts");
            this.setState({
                loadingAccounts: false
            })
            return
        }

        // Append more accounts and stop loading
        this.setState({
            loadingMoreAccounts: false,
            accounts: [...this.state.accounts, ...res.accounts]
        })
    }

    loadInvestmentProviders = async () =>{
        const res = await api.get('/tpa/investmentProvider/list');
        if (!res?.success || !res?.investmentProviders)
            return this.setState({ investmentProviders: [] });
        this.setState({
            investmentProviders: res.investmentProviders
            // loading: false
        });
    }

    // State manipulator functions
    // state.accounts
    // Edit specific field in an account
    editAccountField = async (field, value) => {
        // Must have editing object
        if (!this.state.editingAccount) return;
        this.setState({
            editingAccount: {
                ...this.state.editingAccount,
                [field]: value,
                internalPlanId: this.props.plan.internalPlanId
            }
        });
    }

    // Save account
    saveAccount = async () => {
        const s = this.state;
        const e = {
            ...s.editingAccount,
            pensionProPlanId: this.props.plan.pensionProId,
            planId: this.props.plan._id
        };
        // Trim each value for each key in e 
        for(const key of Object.keys(e)){
            if(typeof e[key] === "string")
                e[key] = e[key].trim();
        }

        
        // Update the isSaving field in editingAccount as true
        this.setState({
            editingAccount: {
                ...s.editingAccount,
                isSaving: true
            }
        })

        // Not editing an account? Just exit
        if (!e || s.editingAccountIdx === -1) return;

        //Need to make sure that the account is valid?

        //If new account - create new account in DB
        if(!e?._id) {
            // TODO: Integrate create new account in DB
            // this.props.loading();

            if (e.number?.length > 25) {
                toast.error("Please ensure that the length of the Account Number is less than 25 characters.");
                return;
            }

            const res = await api.post('/tpa/account/create', { account: e });
            // const ip = this.state.investmentProviders;

            // Update isSaving to false after trying to update the account
            this.setState({
                editingAccount: {
                    ...s.editingAccount,
                    isSaving: false
                }
            })

            if (!res?.success || !res?.data) {
                if (!res.error) { // The response error will already be displayed as a toast
                    toast.error("Failed to create the account.");
                }
                // this.props.loaded();
                return;
            }

            if (this.state.investmentProviders.filter(provider => provider.DisplayName === e.provider).length === 0 ){
                this.setState({investmentProviders: [{"DisplayName": e.provider}, ...this.state.investmentProviders]})
            }

            // filtering out any account that does not have an _id
            const filteredAccounts = s.accounts.filter(account => !!account._id);
            this.setState({
                accounts: [ res.data.account, ...filteredAccounts ],
                editingAccount: null, //TODO: Fix so that once created editingPlan should show planIdx=0
                editingAccountIdx: -1,
            });
            // this.props.loaded();
            return;
        }

        // Not a new account, edit existing investment account
        const account = { ...e };

        // Local copy to update accounts for plan
        const accounts = [...s.accounts];
        for(const key in Object.keys(account)){
            accounts[s.editingAccountIdx][Object.keys(account)[key]] = account[Object.keys(account)[key]]
        }

        if (account.number?.length > 25) {
            toast.error("Please ensure that the length of the Account Number is less than 25 characters.");
            return;
        }

        // Update account in the DB
        const res = await api.post("/tpa/account/update",{
            id: account._id,
            accountDiff: {
                provider: account.provider,
                payee: account.payee,
                internalPlanId: account.internalPlanId,
                number: account.number
            }
        })

        // Update isSaving to false after trying to update the account
        this.setState({
            editingAccount: {
                ...s.editingAccount,
                isSaving: false
            }
        })

        // Response Handling
        if(!res?.success){
            toast.error("Could not create account")
            return;
        }

        // Add investment provider to the list on UI if not created already
        if (this.state.investmentProviders.filter(provider => provider.DisplayName === e.provider).length === 0 ){
            this.setState({investmentProviders: [{"DisplayName": e.provider}, ...this.state.investmentProviders]})
        }

        this.setState({
            accounts,
            editingAccountIdx: -1
        })
    }

    // Delete account
    deleteAccount = async (account, index) => {

        this.props.modal("confirm-caution", {
            message: this.props.ppConnections?.length !== 0 ?
                <>Deleting this account <strong>will</strong> also remove this account from PensionPro.</>
                :
                <>Deleting an account <strong>cannot</strong> be undone. You will need to recreate the account if you wish to restore it.</>,
            action: async() => {
                const s = this.state;


                // Local update
                const accounts = [ ...s.accounts ];
                accounts.splice(index, 1);
                this.setState({
                    accounts,
                    editingAccount: -1,
                });

                // Cloud update
                const res = await api.post('/tpa/account/remove', {id: account._id});

                if (!res) {
                    accounts.splice(index, 0, account);
                    this.setState({
                        accounts,
                        editingAccount: -1,
                    });
                    toast.error("Failed to delete the account.")
                    return
                }
                this.props.modal();

                return
            }
        });
    }

    // Cancel account edit
    cancelAccount = async () => {
        const filteredAccounts = this.state.accounts.filter(account => !!account._id);
        await this.setState({
            editingAccountIdx: null,
            editingAccount: null,
            accounts: filteredAccounts
        });
    }

    render = () => {
        const p = this.props;
        const s = this.state;

        return (
        <div className="plan-editor-page">
            <div className="plan-page-header">
                <h2>Plan Information</h2>
                {p.plan?.cx ? <span className="cx-badge">
                    Synced with CX{p.plan.cx.syncedAt ? " · " + relativeTime(p.plan.cx.syncedAt) : ""}
                </span> : ""}

                {/* Show save/create button if any changes exist */}
                {p.plan && s.updatingPlan ? <>
                    <Button
                        text={p.plan?._id ? "Save" : "Create"}
                        onClick={()=>{
                            this.setState({
                                updatingPlan: false
                            });
                            
                            p.savePlan()
                        }}
                    />
                </> : ""}


                {
                    p.team?.scopes?.includes("TPAPlanEditor")?
                    p.plan?._id ? 
                    <Button 
                        text={"Delete"}
                        onClick={p.deletePlan}
                        style={{
                            backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                            color: "#FF5D00",
                            position: "relative",
                            marginLeft: "auto",
                            marginRight: 20
                        }}
                    />:
                    <Button 
                        text={"Cancel"}
                        onClick={p.cancelPlan}
                        style={{
                            backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                            color: "#FF5D00",
                            position: "relative",
                            marginLeft: "auto",
                            marginRight: 20
                        }}
                    />
                    : null
                }
                {/* Delete button on the far right side */}
                {/* {(p.ppConnections?.length !== 0 && p.ppConnections[0]?.skipPPUpdates === false) ?
                    <p
                        style={{
                            position: "relative",
                            marginLeft: "auto",
                            marginRight: 20
                    }}
                    >
                        Delete plan in PensionPro
                    </p>
                :
                p.plan?._id ? 
                    <Button 
                        text={"Delete"}
                        onClick={p.deletePlan}
                        style={{
                            backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                            color: "#FF5D00",
                            position: "relative",
                            marginLeft: "auto",
                            marginRight: 20
                            
                        }}
                    /> : 
                    <Button 
                    text={"Cancel"}
                    onClick={p.cancelPlan}
                    style={{
                        backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                        color: "#FF5D00",
                        position: "relative",
                        marginLeft: "auto",
                        marginRight: 20
                    }}/> 
                } */}
            </div>
            

            {/* Show all values regarding the selected plan under Plan Information */}
            <div className="plan-editor-information">
                <div className="plan-editor-information-id">
                    <h3>Plan ID</h3>
                    <input
                        type="text"
                        className={p.plan?.internalPlanId?.toLowerCase()?.includes(p.query)? 'QueryMatch': ''}
                        value={p.plan?.internalPlanId??''}
                        onChange={e => {
                            this.setState({
                                updatingPlan: true
                            })
                            p.editPlanField("internalPlanId", e.target.value)
                        }}
                        placeholder="TPA Plan ID..."
                    />
                </div>
                <div className="plan-editor-information-name">
                    <h3>Plan Name</h3>
                    <input
                        type={"text"}
                        className={p.plan?.planName?.toLowerCase().includes(p.query)? 'QueryMatch': ''}
                        value={p.plan ? p.plan.planName : ''}
                        onChange={e => {
                            this.setState({
                                updatingPlan: true
                            })
                            p.editPlanField("planName", e.target.value)
                        }}
                        placeholder="Plan name..."
                    />
                </div>
                <div className="plan-editor-information-type">
                    <h3>Plan Type</h3>
                    <input
                        type={"text"}
                        disabled={this.props.ppConnections?.length !== 0}
                        className={p.plan?.planType?.toLowerCase().includes(p.query)? 'QueryMatch': ''}
                        value={p.plan ? p.plan.planType??"" : ''}
                        onChange={e => {
                            this.setState({
                                updatingPlan: true
                            })
                            p.editPlanField("planType", e.target.value)
                        }}
                        placeholder="Plan Type..."
                    />
                </div>
                <div className="plan-editor-information-pye">
                    <h3>Plan Year End</h3>
                    <PyeCalendarSelector 
                        key={p.plan?._id ?? 'new'}
                        updateMm={p.editPlanField}
                        updateDd={p.editPlanField}
                        endMm={p.plan?.endMm}
                        endDd={p.plan?.endDd}
                        onChange = {(updateVal)=>this.setState({
                            updatingPlan: updateVal
                        })}
                    />
                </div>
                <div className="plan-editor-information-terminated">
                    <Checkbox checked={!p.plan?.active} onToggle={() => {
                        this.setState({
                            updatingPlan: true
                        })
                        
                        // Have a toast if the user is trying to terminate a plan to Pension Pro
                        if(p.plan?.active && this.props.ppConnections?.length !== 0)
                            toast.info("Note: This plan will not be terminated in Pension Pro, please deactivate the plan manually in Pension Pro")
                            
                        p.editPlanField("active",!p.plan?.active)
                    }} />
                    <h3>Terminated</h3>
                </div>
                <div className="plan-editor-information-directory">
                    <h3>Plan Directory</h3>
                    <input
                        type={"text"}
                        className={p.plan?.directory?.toLowerCase().includes(p.query)? 'QueryMatch': ''}
                        value={p.plan ? p.plan.directory ? p.plan.directory : '' : ''}
                        onChange={e => {
                            this.setState({
                                updatingPlan: true
                            })
                            p.editPlanField("directory",e.target.value)
                        }} 
                        placeholder="Path to plan directory..."    
                    />
                </div>
                <div
                    className='plan-editor-information-assignee'
                > 
                    <h3>Plan Assignees</h3>
                    <Select 
                        isMulti
                        className={"multi-select-container"}
                        classNamePrefix={"multi-select"}
                        options={this.props.team?.users?.map((u) => {
                            return {
                                value: u._id,
                                label: u.name
                            }
                        })}
                        value={p.plan?.assignees?.map((a) => {
                            return {
                                value: a,
                                label: p.team?.users?.find((u) => u._id === a)?.name
                            }
                        })}
                        onChange={(e) => {
                            this.setState({
                                updatingPlan: true
                            });
                            p.editPlanField("assignees", e?.map((a) => a.value)??[])
                        }}
                        styles={{
                            valueContainer: (base) => ({
                                ...base,
                                flexWrap: 'nowrap',
                            })
                        }}
                    />
                </div>
            </div>

            {/* Show all the account information for a plan */}
            <div className="plan-editor-account-page">
                {/* Investment account header including button to add a new investment accout */}
                <div className="account-add">
                    <h2>Investment Accounts</h2>
                    <div>
                        {
                            this.props.ppConnections?.length !== 0 ?
                                p.plan.active ? <div
                                className="account-add-icon"
                                onClick={async () => {
                                        // If this is a new plan that hasn't been saved yet, don't allow the user to add an account
                                        if (!p.plan?._id || !p.plan?.internalPlanId?.length) {
                                            return toast.error("Please save the plan with a valid unique plan ID before adding an account.");
                                        }

                                        // If we are currently editing a new investment account, discard changes first
                                        if (s.editingAccountIdx >= 0)
                                            await this.cancelAccount();

                                        this.setState({
                                            editingAccount: newAccount,
                                            accounts: [{...newAccount}, ...this.state.accounts],
                                            editingAccountIdx: 0
                                        })
                                    }}
                                >
                                    Add an investment account
                                </div> : ""
                            : <div
                                className="account-add-icon"
                                onClick={async () => {
                                        // If this is a new plan that hasn't been saved yet, don't allow the user to add an account
                                        if (!p.plan?._id || !p.plan?.internalPlanId?.length) {
                                            return toast.error("Please save the plan with a valid unique plan ID before adding an account.");
                                        }

                                        // If we are currently editing a new investment account, discard changes first
                                        if (s.editingAccountIdx >= 0)
                                            await this.cancelAccount();

                                        this.setState({
                                            editingAccount: newAccount,
                                            accounts: [{...newAccount}, ...this.state.accounts],
                                            editingAccountIdx: 0
                                        })
                                    }}
                                >
                                    Add an investment account
                                </div>
                        }
                    </div>
                </div>
                {s.loadingAccounts ? <div className="plan-editor-loading"
                ><Loading
                    type="spin"
                    color="var(--color-primary)"
                    height={32}
                    width={32}
                />
                    <div className="plan-editor-loading-text">Loading...</div>
                </div> : 
                <div>
                    <Sortable
                        className="plan-editor-accounts-list"
                        title={null}
                        data={s.accounts}
                        scrollbar = {true}  // Include scrollbar in sortable

                        // Default sort
                        // defaultSortBy="provider"
                        // defaultSortDir={1}
                        disableSort={true}
                        // preserveLocationsByKey="_id"
                        emptyMessage={"This plan does not have any investment accounts"}

                        // Header configuration
                        header={{
                            name: {
                                prop: "provider",
                                label: "Provider",
                                width: 220
                            },
                            left: [
                                {
                                    label: "Account Number",
                                    prop: "number",
                                    width: 240
                                },
                                {
                                    prop: "payee",
                                    label: "Payee",
                                    width: 200
                                }
                            ],
                            meta: [
                                {
                                    prop: "",
                                    label: "",
                                    width: 200
                                }
                            ],
                            minWidth: 900
                        }}

                        renderItem={(account, idx, renderMeta, renderLeft) => <AccountCard
                            className="plan-editor-account-card"
                            ppConnections = {this.props.ppConnections}
                            key={idx}
                            isPlanActive={p.plan.active}
                            pensionProPlanId={p.plan.pensionProId}
                            account={account}
                            query={p.query}
                            renderMeta={renderMeta}
                            renderLeft={renderLeft}
                            isEditing = {idx === s.editingAccountIdx}
                            investmentProviders = {s.investmentProviders}
                            editingAccount = {s.editingAccount}
                            editingAccountField={this.editAccountField}
                            onClickCancel = {this.cancelAccount}
                            onClickDelete = {() => {this.deleteAccount(account, idx)}}   
                            onClickSave={this.saveAccount}
                            onClickEdit={async () => {
                                // Save currently editing account
                                // await this.saveAccount();

                                // Discard currently editing account
                                await this.cancelAccount();

                                this.setState({
                                    editingAccount: account,
                                    editingAccountIdx: idx
                                })}
                            }              
                        />}
                    />
                    {this.state.accounts.filter((a)=>a._id).length !== this.state.maxAccountsCount ? 
                        this.state.loadingMoreAccounts ? 
                            <div className="search-loading">
                                <Loading
                                    type="bubbles"
                                    color="var(--color-primary)"
                                    height={60}
                                    width={60}
                                />
                            </div>:
                            <div
                                className="load-more-accounts"
                                onClick={(event)=>this.loadMoreAccounts(this.props.plan, event)}
                            >Load more accounts...</div> : ""
                    }
                </div>}
            </div>
        </div>);
    }

}